import { useMutation, useQuery } from '@tanstack/react-query'
import moment from 'moment'
import { store } from '../..'
import { queryClient } from '../../..'
import { performances } from '../../../api/hr/performance'
import { setAlert, setIsLoading } from '../../components/components-slice'

export function useGetPerformanceSummary() {
  return useQuery(['performance-summary'], () => {
    return performances.getPerformanceSummary()
  })
}

export function useGetPerformances(
  limit,
  page,
  statusFilter,
  search,
  source,
  source_id
) {
  return useQuery(
    ['performances', { limit, page, statusFilter, search, source, source_id }],
    () => {
      store.dispatch(setIsLoading(true))
      return performances.getPerformances({
        limit,
        page,
        statusFilter,
        search,
        source,
        source_id,
      })
    },
    {
      select: (data) => {
        data.data.forEach((performance) => {
          performance.employeeName = `${performance.first_name} ${performance.last_name}`
          performance.period = `${moment(performance.evaluation_start).format(
            'MMMM D, YYYY'
          )} to ${moment(performance.evaluation_end).format('MMMM D, YYYY')}`
          performance.created = moment(performance.created).format(
            'MMMM D, YYYY'
          )
          performance.computedActions = [0, 1, 2]
        })
        return data
      },
      onSettled: (data, error, variables, context) => {
        store.dispatch(setIsLoading(false))
      },
      // keepPreviousData: true
    }
  )
}

export function useGetPerformancesBySource(
  limit,
  page,
  statusFilter,
  search,
  source,
  source_id
) {
  return useQuery(
    [
      'performances-by-source',
      { limit, page, statusFilter, search, source, source_id },
    ],
    () => {
      store.dispatch(setIsLoading(true))
      return performances.getPerformancesBySource({
        source,
        source_id,
        limit,
        page,
        statusFilter,
        search,
      })
    },
    {
      enabled: source_id !== null,
      select: (data) => {
        data.data.forEach((performance) => {
          performance.employeeName = `${performance.first_name} ${performance.last_name}`
          performance.period = `${moment(performance.evaluation_start).format(
            'MMMM D, YYYY'
          )} to ${moment(performance.evaluation_end).format('MMMM D, YYYY')}`
          performance.created = moment(performance.created).format(
            'MMMM D, YYYY'
          )
          performance.computedActions = [0, 1, 2]
        })
        return data
      },
      onSettled: (data, error, variables, context) => {
        store.dispatch(setIsLoading(false))
      },
      // keepPreviousData: true
    }
  )
}

export function useAddPerformanceEvaluation() {
  return useMutation(
    (payload) => {
      store.dispatch(setIsLoading(true))
      return performances.evaluatePerformance(payload)
    },
    {
      onSuccess: (response, variables, context) => {
        queryClient.invalidateQueries('performances')
        store.dispatch(setAlert(true, 'success', response.message))
      },
      onSettled: (data, error, variables, context) => {
        store.dispatch(setIsLoading(false))
      },
    }
  )
}

export function useAddAdvancedPerformanceEvaluation() {
  return useMutation(
    (payload) => {
      store.dispatch(setIsLoading(true))
      return performances.advancedEvaluatePerformance(payload)
    },
    {
      onSuccess: (response, variables, context) => {
        queryClient.invalidateQueries('performances')
        store.dispatch(setAlert(true, 'success', response.message))
      },
      onSettled: (data, error, variables, context) => {
        store.dispatch(setIsLoading(false))
      },
    }
  )
}

export function useGradeBasicPerformance() {
  return useMutation(
    (payload) => {
      store.dispatch(setIsLoading(true))
      return performances.gradeBasicPerformance(payload)
    },
    {
      onSuccess: (response, variables, context) => {
        queryClient.invalidateQueries('performances')
        store.dispatch(setAlert(true, 'success', response.message))
      },
      onSettled: (data, error, variables, context) => {
        store.dispatch(setIsLoading(false))
      },
    }
  )
}

export function useUpdateTrainingRequirements() {
  return useMutation(
    (payload) => {
      store.dispatch(setIsLoading(true))
      return performances.updateTrainingRequirements(payload)
    },
    {
      onSuccess: (response, variables, context) => {
        queryClient.invalidateQueries('performances')
        store.dispatch(setAlert(true, 'success', response.message))
      },
      onSettled: (data, error, variables, context) => {
        store.dispatch(setIsLoading(false))
      },
    }
  )
}

export function useUpdateEmployeeComment() {
  return useMutation(
    (payload) => {
      store.dispatch(setIsLoading(true))
      return performances.updateReviewerComment(payload)
    },
    {
      onSuccess: (response, variables, context) => {
        queryClient.invalidateQueries('performances')
        store.dispatch(setAlert(true, 'success', response.message))
      },
      onSettled: (data, error, variables, context) => {
        store.dispatch(setIsLoading(false))
      },
    }
  )
}

export function useUpdateTeamLeadComment() {
  return useMutation(
    (payload) => {
      store.dispatch(setIsLoading(true))
      return performances.updateReviewerComment(payload)
    },
    {
      onSuccess: (response, variables, context) => {
        queryClient.invalidateQueries('performances')
        store.dispatch(setAlert(true, 'success', response.message))
      },
      onSettled: (data, error, variables, context) => {
        store.dispatch(setIsLoading(false))
      },
    }
  )
}

export function useDeletePerformance() {
  return useMutation(
    (payload) => {
      return performances.deletePerformance(payload)
    },
    {
      onSuccess: (response) => {
        queryClient.invalidateQueries('performances')
        store.dispatch(setAlert(true, 'success', response.message))
      },
    }
  )
}

export function useGetPerformance(id) {
  return useQuery(
    ['performance', { id }],
    () => {
      return performances.getPerformance(id)
    },
    {
      onSettled: (data, error, variables, context) => {
        store.dispatch(setIsLoading(false))
      },
    }
  )
}

export function useGetPerformanceBySource(id) {
  return useQuery(
    ['performance-by-source', { id }],
    () => {
      return performances.getPerformanceBySource(id)
    },
    {
      onSettled: (data, error, variables, context) => {
        store.dispatch(setIsLoading(false))
      },
    }
  )
}

export function useGetPerformanceByEmployee(
  source,
  sourceId,
  limit,
  page,
  statusFilter,
  search,
  start_date,
  end_date
) {
  return useQuery(
    [
      'performance-by-employee',
      {
        source,
        sourceId,
        limit,
        page,
        statusFilter,
        search,
        start_date,
        end_date,
      },
    ],
    () => {
      store.dispatch(setIsLoading(true))
      return performances.getPerformancesByEmployee({
        source,
        sourceId,
        limit,
        page,
        statusFilter,
        search,
        start_date,
        end_date,
      })
    },
    {
      enabled: !!sourceId && !!source,
      select(data) {
        data?.data.forEach((performance) => {
          performance.employeeName = `${performance.employee_first_name} ${performance.employee_last_name}`
          performance.created = `${performance.created.split('T')[0]}`
        })
        return data
      },
      onSettled: (data, error, variables, context) => {
        store.dispatch(setIsLoading(false))
      },
    }
  )
}

export function useGetPerformanceWorkflows(limit, page, search) {
  return useQuery(
    ['performance-workflows', { limit, page, search }],
    () => {
      store.dispatch(setIsLoading(true))
      return performances.getPerformanceWorkflows({
        limit,
        page,
        search,
      })
    },
    {
      select: (data) => {
        data.data.forEach((performance) => {
          performance.created = performance.created?.split('T')[0]
          performance.computedActions = [0, 1, 2]
        })
        return data
      },
      onSettled: (data, error, variables, context) => {
        store.dispatch(setIsLoading(false))
      },
      // keepPreviousData: true
    }
  )
}

export function useGetPerformanceOnboardingWorkflows(limit, page, search) {
  return useQuery(
    ['evaluation-creation-workflows', { limit, page, search }],
    () => {
      store.dispatch(setIsLoading(true))
      return performances.getPerformanceWorkflows({
        limit,
        page,
        search,
      })
    },
    {
      select: (data) => {
        let newData = []
        newData.push({
          value: '',
          label: 'No Workflow Selected',
          criteria: [],
          id: null,
        })
        data.data.forEach((item) => {
          newData.push({
            value: item.id,
            label: item.name,
            criteria: item.sub_workflows,
            id: item.id,
          })
        })
        return newData
      },
      onSettled: (data, error, variables, context) => {
        store.dispatch(setIsLoading(false))
      },
      // keepPreviousData: true
    }
  )
}

export function useGetPerformanceWorkflow(id) {
  return useQuery(
    ['performance-workflow', { id }],
    () => {
      return performances.getPerformanceWorkflow(id)
    },
    {
      enabled: id !== null,
      onSettled: (data, error, variables, context) => {
        store.dispatch(setIsLoading(false))
      },
    }
  )
}

export function useAddPerformanceWorkflow() {
  return useMutation(
    (payload) => {
      return performances.addPerformanceWorkflow(payload)
    },
    {
      onSuccess: (response, variables, context) => {
        queryClient.invalidateQueries('performance-workflows')
        store.dispatch(setAlert(true, 'success', response.success))
      },
      onSettled: (data, error, variables, context) => {
        store.dispatch(setIsLoading(false))
      },
      // keepPreviousData: true
    }
  )
}

export function useEditPerformanceWorkflow() {
  return useMutation(
    (payload) => {
      store.dispatch(setIsLoading(true))
      return performances.updatePerformanceWorkflow(payload)
    },
    {
      onSuccess: (response, variables, context) => {
        queryClient.invalidateQueries('performance-workflows')
        store.dispatch(setAlert(true, 'success', response.message))
      },
      onSettled: (data, error, variables, context) => {
        store.dispatch(setIsLoading(false))
      },
    }
  )
}

export function useMarkPerformanceReviewed() {
  return useMutation(
    (payload) => {
      store.dispatch(setIsLoading(true))
      return performances.markPerformanceReviewed(payload)
    },
    {
      onSuccess: (response, variables, context) => {
        queryClient.invalidateQueries('performances')
        queryClient.invalidateQueries('performance')
        store.dispatch(
          setAlert(true, 'success', 'Performance review successfully')
        )
      },
      onSettled: (data, error, variables, context) => {
        store.dispatch(setIsLoading(false))
      },
    }
  )
}

//OKR Data

export function useGetPerformanceOKRS(source, source_id, limit, page, search) {
  return useQuery(
    ['performance-okrs', { source, source_id, limit, page, search }],
    () => {
      store.dispatch(setIsLoading(true))
      return performances.getPerformanceOkrs({
        source,
        source_id,
        limit,
        page,
        search,
      })
    },
    {
      enabled: source_id !== null,
      select: (data) => {
        data.data.forEach((performance) => {
          performance.period = `${moment(performance.start_date).format(
            'MMMM D, YYYY'
          )} to ${moment(performance.end_date).format('MMMM D, YYYY')}`
          performance.created = moment(performance.created).format(
            'MMMM D, YYYY'
          )
          performance.computedActions = [0, 1, 2]
        })
        return data
      },
      onSettled: (data, error, variables, context) => {
        store.dispatch(setIsLoading(false))
      },
      // keepPreviousData: true
    }
  )
}

export function useAddPerformanceOKR() {
  return useMutation(
    (payload) => {
      return performances.addPerformanceOKR(payload)
    },
    {
      onSuccess: (response, variables, context) => {
        queryClient.invalidateQueries('performance-okrs')
        store.dispatch(setAlert(true, 'success', response.success))
      },
      onSettled: (data, error, variables, context) => {
        store.dispatch(setIsLoading(false))
      },
      // keepPreviousData: true
    }
  )
}

export function useEditPerformanceOKR() {
  return useMutation(
    (payload) => {
      store.dispatch(setIsLoading(true))
      return performances.updatePerformanceOKR(payload)
    },
    {
      onSuccess: (response, variables, context) => {
        queryClient.invalidateQueries('performance-okrs')
        store.dispatch(setAlert(true, 'success', response.message))
      },
      onSettled: (data, error, variables, context) => {
        store.dispatch(setIsLoading(false))
      },
    }
  )
}

export function useDeleteOKR() {
  return useMutation(
    (payload) => {
      return performances.deletePerformanceOKR(payload)
    },
    {
      onSuccess: (response) => {
        queryClient.invalidateQueries('performance-okrs')
        store.dispatch(setAlert(true, 'success', response.message))
      },
    }
  )
}

//Smart Goals
export function useGetPerformanceSmartGoals(
  source,
  source_id,
  limit,
  page,
  search
) {
  return useQuery(
    ['performance-smart-goals', { source, source_id, limit, page, search }],
    () => {
      store.dispatch(setIsLoading(true))
      return performances.getPerformanceSmartGoals({
        source,
        source_id,
        limit,
        page,
        search,
      })
    },
    {
      enabled: source_id !== null,
      select: (data) => {
        data.data.forEach((performance) => {
          performance.period = `${moment(performance.start_date).format(
            'MMMM D, YYYY'
          )} to ${moment(performance.end_date).format('MMMM D, YYYY')}`
          performance.created = moment(performance.created).format(
            'MMMM D, YYYY'
          )
          performance.computedActions = [0, 1, 2]
        })
        return data
      },
      onSettled: (data, error, variables, context) => {
        store.dispatch(setIsLoading(false))
      },
      // keepPreviousData: true
    }
  )
}

export function useAddPerformanceSmartGoals() {
  return useMutation(
    (payload) => {
      return performances.addPerformanceSmartGoals(payload)
    },
    {
      onSuccess: (response, variables, context) => {
        queryClient.invalidateQueries('performance-smart-goals')
        store.dispatch(setAlert(true, 'success', response.success))
      },
      onSettled: (data, error, variables, context) => {
        store.dispatch(setIsLoading(false))
      },
      // keepPreviousData: true
    }
  )
}

export function useEditPerformanceSmartGoals() {
  return useMutation(
    (payload) => {
      store.dispatch(setIsLoading(true))
      return performances.updatePerformanceSmartGoals(payload)
    },
    {
      onSuccess: (response, variables, context) => {
        queryClient.invalidateQueries('performance-smart-goals')
        store.dispatch(setAlert(true, 'success', response.message))
      },
      onSettled: (data, error, variables, context) => {
        store.dispatch(setIsLoading(false))
      },
    }
  )
}

//Technical Appraisals
export function useGetTechnicalAppraisal(
  source,
  source_id,
  limit,
  page,
  search
) {
  return useQuery(
    ['technical-appraisals', { source, source_id, limit, page, search }],
    () => {
      store.dispatch(setIsLoading(true))
      return performances.getTechnicalAppraisals({
        source,
        source_id,
        limit,
        page,
        search,
      })
    },
    {
      enabled: source_id !== null,
      select: (data) => {
        data.data.forEach((performance) => {
          performance.created = moment(performance.created).format(
            'MMMM D, YYYY'
          )
          performance.computedActions = [0, 1, 2]
        })
        return data
      },
      onSettled: (data, error, variables, context) => {
        store.dispatch(setIsLoading(false))
      },
      // keepPreviousData: true
    }
  )
}

//Grading starts
export function useGradeSmartGoals() {
  return useMutation(
    (payload) => {
      store.dispatch(setIsLoading(true))
      return performances.gradeSmartGoals(payload)
    },
    {
      onSuccess: (response, variables, context) => {
        queryClient.invalidateQueries('performances')
        queryClient.invalidateQueries('performance')
        store.dispatch(
          setAlert(true, 'success', 'Grade submitted successfully')
        )
      },
      onSettled: (data, error, variables, context) => {
        store.dispatch(setIsLoading(false))
      },
    }
  )
}

export function useGradeOkrs() {
  return useMutation(
    (payload) => {
      store.dispatch(setIsLoading(true))
      return performances.gradeOkrs(payload)
    },
    {
      onSuccess: (response, variables, context) => {
        queryClient.invalidateQueries('performances')
        queryClient.invalidateQueries('performance')
        store.dispatch(
          setAlert(true, 'success', 'Grade submitted successfully')
        )
      },
      onSettled: (data, error, variables, context) => {
        store.dispatch(setIsLoading(false))
      },
    }
  )
}

export function useGradeBalancedScorecard() {
  return useMutation(
    (payload) => {
      store.dispatch(setIsLoading(true))
      return performances.gradeBalancedScoreCard(payload)
    },
    {
      onSuccess: (response, variables, context) => {
        queryClient.invalidateQueries('performances')
        queryClient.invalidateQueries('performance')
        store.dispatch(
          setAlert(true, 'success', 'Grade submitted successfully')
        )
      },
      onSettled: (data, error, variables, context) => {
        store.dispatch(setIsLoading(false))
      },
    }
  )
}

export function useTeamLeadGradeBalancedScorecard() {
  return useMutation(
    (payload) => {
      store.dispatch(setIsLoading(true))
      return performances.teamLeadGradeBalancedScoreCard(payload)
    },
    {
      onSuccess: (response, variables, context) => {
        queryClient.invalidateQueries('performances')
        queryClient.invalidateQueries('performance')
        store.dispatch(
          setAlert(true, 'success', 'Grade submitted successfully')
        )
      },
      onSettled: (data, error, variables, context) => {
        store.dispatch(setIsLoading(false))
      },
    }
  )
}

export function useGradeOrganisationalAssesssment() {
  return useMutation(
    (payload) => {
      store.dispatch(setIsLoading(true))
      return performances.gradeOrganisationalAssessments(payload)
    },
    {
      onSuccess: (response, variables, context) => {
        queryClient.invalidateQueries('performances')
        queryClient.invalidateQueries('performance')
        store.dispatch(
          setAlert(true, 'success', 'Grade submitted successfully')
        )
      },
      onSettled: (data, error, variables, context) => {
        store.dispatch(setIsLoading(false))
      },
    }
  )
}

export function useTeamLeadGradeSmartGoals() {
  return useMutation(
    (payload) => {
      store.dispatch(setIsLoading(true))
      return performances.teamLeadGradeSmartGoals(payload)
    },
    {
      onSuccess: (response, variables, context) => {
        queryClient.invalidateQueries('performances')
        queryClient.invalidateQueries('performance')
        store.dispatch(
          setAlert(true, 'success', 'Grade submitted successfully')
        )
      },
      onSettled: (data, error, variables, context) => {
        store.dispatch(setIsLoading(false))
      },
    }
  )
}

export function useTeamLeadGradeOkrs() {
  return useMutation(
    (payload) => {
      store.dispatch(setIsLoading(true))
      return performances.teamLeadGradeOkrs(payload)
    },
    {
      onSuccess: (response, variables, context) => {
        queryClient.invalidateQueries('performances')
        queryClient.invalidateQueries('performance')
        store.dispatch(
          setAlert(true, 'success', 'Grade submitted successfully')
        )
      },
      onSettled: (data, error, variables, context) => {
        store.dispatch(setIsLoading(false))
      },
    }
  )
}

export function useTeamLeadGradeOrganisationalAssessments() {
  return useMutation(
    (payload) => {
      store.dispatch(setIsLoading(true))
      return performances.teamLeadGradeOrganisationalAssessments(payload)
    },
    {
      onSuccess: (response, variables, context) => {
        queryClient.invalidateQueries('performances')
        queryClient.invalidateQueries('performance')
        store.dispatch(
          setAlert(true, 'success', 'Grade submitted successfully')
        )
      },
      onSettled: (data, error, variables, context) => {
        store.dispatch(setIsLoading(false))
      },
    }
  )
}

export function useGradeTechnicalAppraisals() {
  return useMutation(
    (payload) => {
      store.dispatch(setIsLoading(true))
      return performances.gradeTechnicalAppraisal(payload)
    },
    {
      onSuccess: (response, variables, context) => {
        queryClient.invalidateQueries('performances')
        queryClient.invalidateQueries('performance')
        store.dispatch(
          setAlert(true, 'success', 'Grade submitted successfully')
        )
      },
      onSettled: (data, error, variables, context) => {
        store.dispatch(setIsLoading(false))
      },
    }
  )
}

export function useTeamLeadGradeTechnicalAppraisals() {
  return useMutation(
    (payload) => {
      store.dispatch(setIsLoading(true))
      return performances.teamLeadGradeTechnicalAppraisal(payload)
    },
    {
      onSuccess: (response, variables, context) => {
        queryClient.invalidateQueries('performances')
        queryClient.invalidateQueries('performance')
        store.dispatch(
          setAlert(true, 'success', 'Grade submitted successfully')
        )
      },
      onSettled: (data, error, variables, context) => {
        store.dispatch(setIsLoading(false))
      },
    }
  )
}
//Grading Ends

//Organisational Appraisal
export function useGetPerformanceTechnicalAppraisal(type) {
  return useQuery(
    ['performance-technical-appraisal', { type }],
    () => {
      store.dispatch(setIsLoading(true))
      return performances.getPerformanceAppraisalIndicators({ type })
    },
    {
      onSettled: (data, error, variables, context) => {
        store.dispatch(setIsLoading(false))
      },
      // keepPreviousData: true
    }
  )
}

export function useGetPerformanceBehaviouralAppraisal(type) {
  return useQuery(
    ['performance-behavioural-appraisal', { type }],
    () => {
      store.dispatch(setIsLoading(true))
      return performances.getPerformanceAppraisalIndicators({ type })
    },
    {
      onSettled: (data, error, variables, context) => {
        store.dispatch(setIsLoading(false))
      },
      // keepPreviousData: true
    }
  )
}

export function useAddPerformanceIndicator() {
  return useMutation(
    (payload) => {
      return performances.addPerformanceAppraisalIndicators(payload)
    },
    {
      onSuccess: (response, variables, context) => {
        queryClient.invalidateQueries('performance-okrs')
        store.dispatch(setAlert(true, 'success', response.success))
      },
      onSettled: (data, error, variables, context) => {
        store.dispatch(setIsLoading(false))
      },
      // keepPreviousData: true
    }
  )
}

export function useEditPerformanceIndicator() {
  return useMutation(
    (payload) => {
      store.dispatch(setIsLoading(true))
      return performances.updatePerformanceAppraisalIndicators(payload)
    },
    {
      onSuccess: (response, variables, context) => {
        queryClient.invalidateQueries('performance-okrs')
        store.dispatch(setAlert(true, 'success', response.message))
      },
      onSettled: (data, error, variables, context) => {
        store.dispatch(setIsLoading(false))
      },
    }
  )
}

export function useGradeOrganisationalIndicators() {
  return useMutation(
    (payload) => {
      store.dispatch(setIsLoading(true))
      return performances.gradeOrganisationalAssessments(payload)
    },
    {
      onSuccess: (response, variables, context) => {
        queryClient.invalidateQueries('performances')
        queryClient.invalidateQueries('performance')
        store.dispatch(setAlert(true, 'success', response.message))
      },
      onSettled: (data, error, variables, context) => {
        store.dispatch(setIsLoading(false))
      },
    }
  )
}

export function useDeletePerformanceIndicator() {
  return useMutation(
    (payload) => {
      return performances.deletePerformanceAppraisalIndicators(payload)
    },
    {
      onSuccess: (response) => {
        queryClient.invalidateQueries('performance-okrs')
        store.dispatch(setAlert(true, 'success', response.message))
      },
    }
  )
}

export function useGetAssignedPerformances(limit, page, statusFilter, search) {
  return useQuery(
    ['assigned', { limit, page, statusFilter, search }],
    () => {
      store.dispatch(setIsLoading(true))
      return performances.getAssignedPerformances({
        limit,
        page,
        statusFilter,
        search,
      })
    },
    {
      select: (data) => {
        data.data.forEach((performance) => {
          performance.employeeName = `${performance.first_name} ${performance.last_name}`
          performance.period = `${moment(performance.evaluation_start).format(
            'MMMM D, YYYY'
          )} to ${moment(performance.evaluation_end).format('MMMM D, YYYY')}`
          performance.created = moment(performance.created).format(
            'MMMM D, YYYY'
          )
          performance.deadline = `${moment(performance.deadline).format(
            'MMMM D, YYYY'
          )}`
          if (performance.status === 0) {
            performance.computedActions = [0]
          } else {
            performance.computedActions = [1]
          }
        })
        return data
      },
      onSettled: (data, error, variables, context) => {
        store.dispatch(setIsLoading(false))
      },
      // keepPreviousData: true
    }
  )
}

export function useGetEvaluations(limit, page, statusFilter, search) {
  return useQuery(
    ['performances', { limit, page, statusFilter, search }],
    () => {
      store.dispatch(setIsLoading(true))
      return performances.getEvaluations({
        limit,
        page,
        statusFilter,
        search,
      })
    },
    {
      select: (data) => {
        data.data.forEach((performance) => {
          performance.employeeName = `${performance.first_name} ${performance.last_name}`
          performance.period = `${moment(performance.evaluation_start).format(
            'MMMM D, YYYY'
          )} to ${moment(performance.evaluation_end).format('MMMM D, YYYY')}`
          performance.created = moment(performance.created).format(
            'MMMM D, YYYY'
          )
          performance.computedActions = [0, 1]
        })
        return data
      },
      onSettled: (data, error, variables, context) => {
        store.dispatch(setIsLoading(false))
      },
      // keepPreviousData: true
    }
  )
}

export function useAddGoal() {
  return useMutation(
    (payload) => {
      store.dispatch(setIsLoading(true))
      return performances.addGoal(payload)
    },
    {
      onSuccess: (response, variables, context) => {
        queryClient.invalidateQueries('goals')
        store.dispatch(setAlert(true, 'success', response.message))
      },
      onSettled: (data, error, variables, context) => {
        store.dispatch(setIsLoading(false))
      },
    }
  )
}


export function useEditGoalsInfo() {
  return useMutation(
    (payload) => {
      store.dispatch(setIsLoading(true))
      return performances.editGoalsInfo(payload)
    },
    {
      onSuccess: (response, variables, context) => {
        queryClient.invalidateQueries('goals')
        store.dispatch(setAlert(true, 'success', response.message))
      },
      onSettled: (data, error, variables, context) => {
        store.dispatch(setIsLoading(false))
      },
    }
  )
}

export function useAddSubGoal() {
  return useMutation(
    (payload) => {
      store.dispatch(setIsLoading(true))
      return performances.addSubGoal(payload)
    },
    {
      onSuccess: (response, variables, context) => {
        queryClient.invalidateQueries('add-sub-goal')
        store.dispatch(setAlert(true, 'success', response.message))
      },
      onSettled: (data, error, variables, context) => {
        store.dispatch(setIsLoading(false))
      },
    }
  )
}

export function useEditSubGoal() {
  return useMutation(
    (payload) => {
      store.dispatch(setIsLoading(true))
      return performances.editSubGoal(payload)
    },
    {
      onSuccess: (response, variables, context) => {
        queryClient.invalidateQueries('sub-goals')
        store.dispatch(setAlert(true, 'success', response.message))
      },
      onSettled: (data, error, variables, context) => {
        store.dispatch(setIsLoading(false))
      },
    }
  )
}

export function useUpdateGoal() {
  return useMutation(
    (payload) => {
      store.dispatch(setIsLoading(true))
      return performances.updateGoal(payload)
    },
    {
      onSuccess: (response, variables, context) => {
        queryClient.invalidateQueries('update-goal')
        store.dispatch(setAlert(true, 'success', response.message))
      },
      onSettled: (data, error, variables, context) => {
        store.dispatch(setIsLoading(false))
      },
    }
  )
}

export function useExtendGoal() {
  return useMutation(
    (payload) => {
      store.dispatch(setIsLoading(true))
      return performances.extendGoal(payload)
    },
    {
      onSuccess: (response, variables, context) => {
        queryClient.invalidateQueries('extend-goal')
        store.dispatch(setAlert(true, 'success', response.message))
      },
      onSettled: (data, error, variables, context) => {
        store.dispatch(setIsLoading(false))
      },
    }
  )
}

export function useExtendSubGoal() {
  return useMutation(
    (payload) => {
      store.dispatch(setIsLoading(true))
      return performances.extendSubGoal(payload)
    },
    {
      onSuccess: (response, variables, context) => {
        queryClient.invalidateQueries('extend-sub-goal')
        store.dispatch(setAlert(true, 'success', response.message))
      },
      onSettled: (data, error, variables, context) => {
        store.dispatch(setIsLoading(false))
      },
    }
  )
}

export function useAddRemoveMember() {
  return useMutation(
    (payload) => {
      store.dispatch(setIsLoading(true))
      return performances.addRemoveMember(payload)
    },
    {
      onSuccess: (response, variables, context) => {
        queryClient.invalidateQueries('add-remove-member')
        store.dispatch(setAlert(true, 'success', response.message))
      },
      onSettled: (data, error, variables, context) => {
        store.dispatch(setIsLoading(false))
      },
    }
  )
}

export function useAddRemoveAssignee() {
  return useMutation(
    (payload) => {
      store.dispatch(setIsLoading(true))
      return performances.addRemoveAssignee(payload)
    },
    {
      onSuccess: (response, variables, context) => {
        queryClient.invalidateQueries('add-remove-member')
        store.dispatch(setAlert(true, 'success', response.message))
      },
      onSettled: (data, error, variables, context) => {
        store.dispatch(setIsLoading(false))
      },
    }
  )
}

export function useMarkSubGoal() {
  return useMutation(
    (payload) => {
      store.dispatch(setIsLoading(true))
      return performances.markSubGoal(payload)
    },
    {
      onSuccess: (response, variables, context) => {
        queryClient.invalidateQueries('subgoal')
        store.dispatch(setAlert(true, 'success', response.message))
      },
      onSettled: (data, error, variables, context) => {
        store.dispatch(setIsLoading(false))
      },
    }
  )
}

export function useUpdateMembersPermission() {
  return useMutation(
    (payload) => {
      store.dispatch(setIsLoading(true))
      return performances.membersPermission(payload)
    },
    {
      onSuccess: (response, variables, context) => {
        queryClient.invalidateQueries('members-permission')
        store.dispatch(setAlert(true, 'success', response.message))
      },
      onSettled: (data, error, variables, context) => {
        store.dispatch(setIsLoading(false))
      },
    }
  )
}

export function useMarkGoal() {
  return useMutation(
    (payload) => {
      store.dispatch(setIsLoading(true))
      return performances.markGoal(payload)
    },
    {
      onSuccess: (response, variables, context) => {
        queryClient.invalidateQueries('mark-goal')
        store.dispatch(setAlert(true, 'success', response.message))
      },
      onSettled: (data, error, variables, context) => {
        store.dispatch(setIsLoading(false))
      },
    }
  )
}

export function useReviewSubGoal() {
  return useMutation(
    (payload) => {
      store.dispatch(setIsLoading(true))
      return performances.reviewSubGoal(payload)
    },
    {
      onSuccess: (response, variables, context) => {
        queryClient.invalidateQueries('review-subgoal')
        store.dispatch(setAlert(true, 'success', response.message))
      },
      onSettled: (data, error, variables, context) => {
        store.dispatch(setIsLoading(false))
      },
    }
  )
}

export function useDeleteSubGoal() {
  return useMutation(
    (payload) => {
      return performances.deleteSubGoal(payload)
    },
    {
      onSuccess: (response, variables, context) => {
        queryClient.invalidateQueries('delete-subgoal')
        store.dispatch(setAlert(true, 'success', response.message))
      },
      onSettled: (data, error, variables, context) => {
        store.dispatch(setIsLoading(false))
      },
    }
  )
}

export function useDeleteGoal() {
  return useMutation(
    (payload) => {
      return performances.deleteGoal(payload)
    },
    {
      onSuccess: (response, variables, context) => {
        queryClient.invalidateQueries('delete-goal')
        store.dispatch(setAlert(true, 'success', response.message))
      },
      onSettled: (data, error, variables, context) => {
        store.dispatch(setIsLoading(false))
      },
    }
  )
}

export function useGetGoals(limit, page, search) {
  return useQuery(
    ['goals', { limit, page, search }],
    () => {
      store.dispatch(setIsLoading(true))
      return performances.getGoals({
        limit,
        page,
        search,
      })
    },
    {
      select(data) {
        return data
      },
      onSettled: (data, error, variables, context) => {
        store.dispatch(setIsLoading(false))
      },
      // keepPreviousData: true
    }
  )
}

export function useGetTeamGoals(limit, page, search) {
  return useQuery(
    ['team-goals', { limit, page, search }],
    () => {
      store.dispatch(setIsLoading(true))
      return performances.getTeamGoals({
        limit,
        page,
        search,
      })
    },
    {
      select(data) {
        return data
      },
      onSettled: (data, error, variables, context) => {
        store.dispatch(setIsLoading(false))
      },
      // keepPreviousData: true
    }
  )
}

export function useGetSingleGoal(id, limit, page, statusFilter) {
  return useQuery(
    ['single-goal', { id }],
    () => {
      store.dispatch(setIsLoading(true))
      return performances.getSingleGoal({
        id,
        limit,
        page,
        statusFilter,
      })
    },
    {
      select(data) {
        return data
      },
      onSettled: (data, error, variables, context) => {
        store.dispatch(setIsLoading(false))
      },
      // keepPreviousData: true
    }
  )
}

export function useGetSingleTeamGoal(id, limit, page, statusFilter) {
  return useQuery(
    ['single-team-goal', { id }],
    () => {
      store.dispatch(setIsLoading(true))
      return performances.getSingleTeamGoal({
        id,
        limit,
        page,
        statusFilter,
      })
    },
    {
      select(data) {
        return data
      },
      onSettled: (data, error, variables, context) => {
        store.dispatch(setIsLoading(false))
      },
      // keepPreviousData: true
    }
  )
}


